import Part from "@/components/body/parts/part";
import Utils, { Colors } from "@/components/shared/util";
import { Patient } from "@/store/modules/patient/types";
import _ from "lodash";
import SVG, { Container } from "svg.js";
import { Store } from "vuex";
import { BodyConfig } from ".";
import SvgBarGraph from "./svgBarGraph";

const lLungId = "#lLungLevel";
const rLungId = "#rLungLevel";
const ANIMATION_SCALE_START = 1.0;
const ANIMATION_SCALE_END = 1.3;
export default class Lungs extends Part {
  lungsAnimationGroup!: SVG.Element;
  lLung: SVG.Element;
  rLung: SVG.Element;
  co2!: SvgBarGraph;
  spo2!: SvgBarGraph;
  rate = 1;
  loopRunning = false;

  get usingLungRateAtom() {
    return this.config.atomId === "lungRate";
  }

  constructor(config: BodyConfig, container: SVG.Container, store: Store<any>) {
    super(config, container, store);
    const showCO2SPO2 = () => {
      if (this.isInteractive) {
        this.action("O2");
      }
    };

    // Handle click target here (will include bar graph text and bars)
    const clickableAreaContainer = SVG.select(
      "#lungBars",
      this.container.node
    ).first() as Container;

    clickableAreaContainer.click(showCO2SPO2).attr("cursor", "pointer");

    if (!this.usingLungRateAtom) {
      this.co2 = new SvgBarGraph(
        clickableAreaContainer,
        { x: 135, y: 190 },
        { title: this.globalConfig.co2Title },
        this.showGreenGrayOnly
      );
      this.spo2 = new SvgBarGraph(
        clickableAreaContainer,
        { x: 320, y: 190 },
        { title: "SpO₂" },
        this.showGreenGrayOnly
      );
    }

    this.lLung = SVG.select(lLungId, this.container.node).first();
    this.rLung = SVG.select(rLungId, this.container.node).first();
  }

  updateBrightnessMode() {
    super.updateBrightnessMode();
    if (!this.usingLungRateAtom) {
      const color = this.dark ? Colors.White : Colors.Black;
      this.co2.title.attr({ fill: color });
      this.co2.value1.attr({ fill: color });
      this.co2.value2.attr({ fill: color });

      this.spo2.title.attr({ fill: color });
      this.spo2.value1.attr({ fill: color });
      this.spo2.value2.attr({ fill: color });
    }
  }

  getExtraData(patient: Patient) {
    const singleLung = _.get(
      patient.body.current,
      "SingleLungVentilation.value",
      false
    );
    const lungRate = _.get(patient.body.atoms, "lungRate.value", null);

    const co2 = patient.body.atoms[this.globalConfig.co2Atom];
    const spo2 = patient.body.atoms.spo2;
    this.extraData = { singleLung, lungRate, co2, spo2 };
  }

  updateData(patient: Patient | null) {
    super.updateData(patient);

    if (!this.usingLungRateAtom) {
      if (this.extraData.co2) {
        this.co2.updateData(this.extraData.co2);
      } else {
        this.co2.updateData();
      }

      if (this.extraData.spo2) {
        this.spo2.updateData(this.extraData.spo2);
      } else {
        this.spo2.updateData();
      }
    }

    if (!this.atom) {
      return;
    }

    const color = this.extraData.singleLung
      ? Utils.getLevelColor(0, this.showGreenGrayOnly)
      : Utils.getLevelColor(this.atom.level, this.showGreenGrayOnly);
    this.lLung.stroke(color);

    this.rLung.stroke(
      Utils.getLevelColor(this.atom.level, this.showGreenGrayOnly)
    );

    // Breaths per minute
    this.rate =
      this.extraData.lungRate === 0
        ? 0
        : Math.floor((60 / this.extraData.lungRate) * 1000);

    // Animations must be enabled, loop hasn't already started, and a positive rate
    if (this.animated && !this.loopRunning && this.rate > 0) {
      this.loopRunning = true;
      this.animationLoop();
    }

    // Handle the case where lung went from animating to single lung ventilation
    if (this.animations.lLungAnimation && this.extraData.singleLung) {
      this.animations.lLungAnimation.finish();
      delete this.animations.lLungAnimation;
    }
  }

  async animationLoop() {
    if (this.rate === 0) {
      this.loopRunning = false;
      return;
    }
    const animationPromises = [];

    if (!this.extraData.singleLung) {
      const lLungPromise = new Promise(resolve => {
        this.animations.lLungAnimation = this.lLung
          .animate(this.rate, ">")
          .scale(ANIMATION_SCALE_END, ANIMATION_SCALE_END)
          .scale(ANIMATION_SCALE_START, ANIMATION_SCALE_START)
          .after(resolve);
      });
      animationPromises.push(lLungPromise);
    }

    const rLungPromise = new Promise(resolve => {
      this.animations.rLungAnimation = this.rLung
        .animate(this.rate, ">")
        .scale(ANIMATION_SCALE_END, ANIMATION_SCALE_END)
        .scale(ANIMATION_SCALE_START, ANIMATION_SCALE_START)
        .after(resolve);
    });
    animationPromises.push(rLungPromise);

    // synchronize all animations so next loop is never started without them completing first
    await Promise.all(animationPromises);

    // Check if it is cleaning up
    if (!this.cleaningUp) {
      this.animationLoop();
    }
  }
}
